import { parsePhoneNumberFromString } from "libphonenumber-js";

const DEFAULT_COUNTRY = "AU";

export const normalizeCountryCode = (value) => {
  const cleaned = String(value || "").trim();
  return cleaned ? cleaned.toUpperCase() : DEFAULT_COUNTRY;
};

export const inferCountryFromPhoneNumber = (value, defaultCountry = DEFAULT_COUNTRY) => {
  const fallback = normalizeCountryCode(defaultCountry);
  if (!value) return fallback;
  const raw = String(value).trim();
  if (!raw) return fallback;

  try {
    const rawWithPlus = raw.startsWith("+") ? raw : ("+" + raw);
    const parsedDirect = parsePhoneNumberFromString(rawWithPlus);
    if (parsedDirect && parsedDirect.country) {
      return parsedDirect.country;
    }

    const parsedNational = parsePhoneNumberFromString(raw, fallback);
    if (parsedNational && parsedNational.country) {
      return parsedNational.country;
    }

    return fallback;
  } catch (_error) {
    return fallback;
  }
};

export const normalizePhoneNumberForInput = (value, defaultCountry = DEFAULT_COUNTRY) => {
  const fallback = normalizeCountryCode(defaultCountry);
  if (!value) return { phone: "", country: fallback };
  const raw = String(value).trim();
  if (!raw) return { phone: "", country: fallback };

  try {
    // 1. If it already starts with '+', parse it directly
    if (raw.startsWith("+")) {
      const parsed = parsePhoneNumberFromString(raw);
      if (parsed && parsed.isValid() && parsed.country) {
        return {
          phone: parsed.number,
          country: parsed.country,
        };
      }

      // If '+' prefix is not a valid international number (e.g. +12412826843), try parsing digits with fallback country
      const digitsOnly = raw.replace(/\D/g, "");
      const parsedFallback = parsePhoneNumberFromString(digitsOnly, fallback);
      if (parsedFallback && parsedFallback.isValid() && parsedFallback.country) {
        return {
          phone: parsedFallback.number,
          country: parsedFallback.country,
        };
      }

      if (fallback === "AU") {
        if (digitsOnly.startsWith("61")) {
          return { phone: "+" + digitsOnly, country: "AU" };
        }
        const cleanNational = digitsOnly.replace(/^0+/, "");
        return { phone: "+61" + cleanNational, country: "AU" };
      }

      return { phone: "+" + digitsOnly, country: fallback };
    }

    // 2. If it is already an international digits string (e.g. 61411424346)
    const parsedWithPlus = parsePhoneNumberFromString("+" + raw);
    if (parsedWithPlus && parsedWithPlus.isValid()) {
      return {
        phone: parsedWithPlus.number,
        country: parsedWithPlus.country || fallback,
      };
    }

    // 3. If it is a domestic format (e.g. 0411424346 in Australia)
    const parsedNational = parsePhoneNumberFromString(raw, fallback);
    if (parsedNational && parsedNational.isValid()) {
      return {
        phone: parsedNational.number,
        country: parsedNational.country || fallback,
      };
    }

    // 4. Fallback for domestic numbers starting with 0 for default country (e.g. AU)
    if (fallback === "AU" && raw.startsWith("0")) {
      const digitsOnly = raw.replace(/\D/g, "");
      const withoutLeadingZero = digitsOnly.replace(/^0+/, "");
      return {
        phone: "+61" + withoutLeadingZero,
        country: "AU",
      };
    }

    return {
      phone: raw.startsWith("+") ? raw : ("+" + raw),
      country: fallback,
    };
  } catch (_e) {
    return { phone: raw, country: fallback };
  }
};

export const isValidPhoneNumberForCountry = (value, countryCode) => {
  const normalizedCountry = normalizeCountryCode(countryCode);
  let rawValue = String(value || "").trim();

  if (!rawValue) return false;

  // Prepend '+' if missing so libphonenumber-js parses the dial code correctly
  if (!rawValue.startsWith("+")) {
    rawValue = "+" + rawValue;
  }

  try {
    const parsed =
      parsePhoneNumberFromString(rawValue, normalizedCountry) ||
      parsePhoneNumberFromString(rawValue);

    if (!parsed) return false;

    if (
      parsed.country &&
      normalizedCountry &&
      parsed.country !== normalizedCountry
    ) {
      return false;
    }

    return parsed.isValid();
  } catch (_error) {
    return false;
  }
};

export { DEFAULT_COUNTRY };
